import { Box, Group, Text, createStyles, useMantineTheme } from "@mantine/core";
import { RpiAlert, RpiLabelText } from "@rpi/openapi-core";
import React from "react";

export interface BraintreeInputProps {
  optional?: boolean;
  label?: string;
  error?: string | boolean;
  errorMessage: string;
  onFocus?: () => void;
  isFocused?: boolean;
  disabled?: boolean;
}

const useStyles = createStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },
  inputContainer: {
    border: `1px solid ${theme.other.semanticColors.borderSubtle}`,
    "&:hover": {
      background: theme.other.semanticColors.bg.inputHover,
    },
    "&[data-error='true']": {
      border: `1px solid ${theme.other.semanticColors.alertDangerBorder}`,
    },
    "&[data-focused='true']": {
      borderColor: theme.other.semanticColors.borderSubtle,
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
    },
    "&[data-disabled='true']": {
      opacity: 0.6,
      cursor: "not-allowed",
      backgroundColor: theme.other.semanticColors.bg.neutralDisabled,
      pointerEvents: "none",
    },
    borderRadius: theme.radius.sm,
    backgroundColor: theme.other.semanticColors.inputBackground,
    height: 40,
    width: "100%",
    fontFamily: "sofia-pro, sans-serif",
    fontSize: theme.fontSizes.md,
    position: "relative",
  },
}));

export const BraintreeInput = React.forwardRef<HTMLDivElement, BraintreeInputProps>(
  ({ label, optional = false, error, errorMessage, onFocus, isFocused = false, disabled = false }, ref) => {
    const theme = useMantineTheme();
    const { classes } = useStyles();

    return (
      <Box className={classes.container}>
        <Group position="apart">
          <RpiLabelText>{label}</RpiLabelText>
          {optional && (
            <Text size="sm" color={theme.other.semanticColors.textSubtle}>
              Optional
            </Text>
          )}
        </Group>
        <div
          className={classes.inputContainer}
          onClick={disabled ? undefined : onFocus}
          ref={ref}
          data-error={!!error}
          data-focused={isFocused}
          data-disabled={disabled}
        />
        {error && <RpiAlert>{errorMessage}</RpiAlert>}
      </Box>
    );
  }
);
